/**
 * period-repository.js — locking and reopening accounting periods.
 *
 * A LOCK is a date range that ordinary users cannot write into. It exists
 * because a set of books that has been reported on — a GST return filed, a
 * balance sheet handed to a bank — must not silently change afterwards, and the
 * only thing standing between a filed return and a changed figure is somebody
 * remembering not to backdate a voucher.
 *
 * A lock is NOT absolute, deliberately. The specification requires that an
 * authorised user can reopen a period, and it is right: a lock nobody can
 * override is a lock that gets worked around by dating the correction into the
 * next month, which is worse than the edit it prevented because it moves the
 * error somewhere nobody will look for it. Reopening is an owner's act and it
 * is logged.
 *
 * The locks themselves are never deleted. A released lock keeps its dates and
 * carries who released it and when — "was August locked when that voucher was
 * entered?" is a question an audit asks, and a deleted document cannot answer
 * it.
 */

import { limit, orderBy, query, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { periodLockDoc, periodLocksCol } from '../../lib/paths';
import { ACTIVITY_TYPES } from '../../lib/audit';
import { recordActivity } from '../../lib/activity-log';
import { financialYear } from '../../lib/fiscal';
import { formatDate } from '../../lib/format';
import { toDate } from '../../lib/accounting';

export const LOCK_SCHEMA_VERSION = 1;

export class PeriodError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'PeriodError';
    this.code = code;
  }
}

/** The four shapes the specification asks for, expressed as one range. */
export const LOCK_PRESETS = {
  DAY: 'day',
  MONTH: 'month',
  RANGE: 'range',
  FINANCIAL_YEAR: 'financial_year',
};

export function rangeForPreset(preset, reference = new Date()) {
  const date = toDate(reference) || new Date();
  switch (preset) {
    case LOCK_PRESETS.DAY:
      return {
        from: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0),
        to: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999),
        label: formatDate(date),
      };
    case LOCK_PRESETS.MONTH: {
      const from = new Date(date.getFullYear(), date.getMonth(), 1);
      const to = new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);
      return {
        from,
        to,
        label: from.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
      };
    }
    case LOCK_PRESETS.FINANCIAL_YEAR: {
      const fy = financialYear(date);
      return { from: fy.start, to: fy.end, label: `FY ${fy.label}` };
    }
    default:
      return { from: null, to: null, label: '' };
  }
}

/**
 * Lock a period.
 *
 * The document id is derived from the range rather than generated, so locking
 * August twice produces one lock rather than two overlapping ones that both
 * have to be released before anybody can write.
 */
export async function lockPeriod({ businessId, from, to, reason = '', label = '', actor = null }) {
  if (!businessId) throw new PeriodError('Missing business.', 'no-business');
  const start = toDate(from);
  const end = toDate(to);
  if (!start || !end) throw new PeriodError('A lock needs a start and an end date.', 'invalid');
  if (start > end) throw new PeriodError('The start date is after the end date.', 'invalid');

  const id = `${isoDay(start)}_${isoDay(end)}`;
  const now = serverTimestamp();

  await setDoc(
    periodLockDoc(businessId, id),
    {
      schemaVersion: LOCK_SCHEMA_VERSION,
      from: start,
      to: end,
      label: label || `${formatDate(start)} — ${formatDate(end)}`,
      reason: String(reason || '').slice(0, 240),
      isReleased: false,
      releasedAt: null,
      releasedBy: null,
      lockedAt: now,
      lockedBy: actor?.uid || null,
      lockedByName: actor?.name || null,
      updatedAt: now,
    },
    { merge: true }
  );

  await recordActivity({
    businessId,
    actor,
    type: ACTIVITY_TYPES.PERIOD_LOCKED,
    subjectId: id,
    subjectLabel: label || `${formatDate(start)} — ${formatDate(end)}`,
    reason,
  });

  return { id };
}

/**
 * Reopen a locked period.
 *
 * An owner's act. The lock document stays and records who released it — the
 * question "was August locked at the time?" needs an answer, and a deleted
 * document has none.
 */
export async function releasePeriod({ businessId, lockId, actor = null, reason = '' }) {
  if (!businessId || !lockId) throw new PeriodError('Missing lock.', 'no-lock');

  await updateDoc(periodLockDoc(businessId, lockId), {
    isReleased: true,
    releasedAt: serverTimestamp(),
    releasedBy: actor?.uid || null,
    releasedByName: actor?.name || null,
    releaseReason: String(reason || '').slice(0, 240),
    updatedAt: serverTimestamp(),
  });

  await recordActivity({
    businessId,
    actor,
    type: ACTIVITY_TYPES.PERIOD_REOPENED,
    subjectId: lockId,
    subjectLabel: lockId,
    reason,
  });

  return { id: lockId };
}

/** Re-lock a period that was reopened, once the correction is made. */
export async function relockPeriod({ businessId, lockId, actor = null }) {
  await updateDoc(periodLockDoc(businessId, lockId), {
    isReleased: false,
    relockedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await recordActivity({
    businessId,
    actor,
    type: ACTIVITY_TYPES.PERIOD_LOCKED,
    subjectId: lockId,
    subjectLabel: lockId,
  });
  return { id: lockId };
}

export function periodLocksQuery(businessId) {
  if (!businessId) return null;
  return query(periodLocksCol(businessId), orderBy('from', 'desc'), limit(200));
}

/** A stable, sortable, filename-safe day key. */
function isoDay(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}
